import React from "react";
import { Sparkles, Target, FileCheck2, CircleHelp } from "lucide-react";

const Card = ({ label, value, note, testId, icon: Icon, accent = "#0284C7", status }) => (
  <div className="accent-bar card-soft card-soft-hover p-4 pl-5" style={{ "--accent": accent }} data-testid={testId}>
    <div className="flex items-start justify-between gap-2">
      <p className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-500"><Icon className="w-3.5 h-3.5" style={{ color: accent }} /> {label}</p>
      {status && <span className="shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold" style={{ color: accent, borderColor: `${accent}33`, background: `${accent}12` }}>{status}</span>}
    </div>
    <p className="mt-2.5 text-sm font-semibold text-slate-900 break-words leading-snug">{value}</p>
    {note && <p className="mt-1.5 text-xs text-slate-500 break-words">{note}</p>}
  </div>
);

export const AccelSummary = ({ state }) => {
  const caps = state.capabilities || [];
  const confirmed = caps.filter((c) => c.user_status === "confirmed");
  const strongest = confirmed[0] || caps[0];
  const primary = (state.hypotheses || []).find((h) => h.status === "primary");
  const bp = state.blueprint;
  const routes = new Set((state.hypotheses || []).filter((h) => h.status !== "rejected").map((h) => h.archetype_id));
  const nextAction = {
    profile: "Confirm your profile to begin",
    capabilities: "Confirm the capabilities that are true for you",
    routes: "Review the income routes and generate hypotheses",
    direction: "Choose your primary direction",
    blueprint: "Shape your offer and save your blueprint",
    validate: state.handoff ? "Handoff saved — the 10K Validation Sprint is next" : "Prepare your validation handoff",
  }[state.progress?.step];

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-testid="accelerator-summary">
      <Card testId="accel-card-capability" icon={Sparkles} label="Your strongest capability" accent="#0284C7"
        status={confirmed.length ? "Confirmed" : "Pending"}
        value={strongest ? strongest.name : "Not identified yet"}
        note={strongest ? `${confirmed.length} confirmed of ${caps.length} found` : "Run capability extraction"} />
      <Card testId="accel-card-routes" icon={CircleHelp} label="Potential income routes" accent="#0D9488"
        status={routes.size ? "Ready" : "Not started"}
        value={routes.size ? `${routes.size} route${routes.size === 1 ? "" : "s"} worth testing` : "Not generated yet"}
        note={state.progress?.hypotheses ? `${state.progress.hypotheses} hypotheses` : "Hypotheses, not market evidence"} />
      <Card testId="accel-card-primary" icon={Target} label="Primary direction" accent={primary ? "#059669" : "#64748B"}
        status={primary ? "Chosen" : "Your call"}
        value={primary ? primary.title : "You haven't chosen yet"}
        note={bp?.price_hypothesis?.amount ? `Price hypothesis: ${bp.target_currency === "USD" ? "$" : "₹"}${Number(bp.price_hypothesis.amount).toLocaleString()}` : "Your choice is authoritative"} />
      <Card testId="accel-card-next" icon={FileCheck2} label="Next action" accent="#D97706" status="Do next" value={nextAction || "—"}
        note={bp ? `Blueprint v${bp.version} · target ${bp.target_currency === "USD" ? "$" : "₹"}${Number(bp.target_amount).toLocaleString()}` : "Target additional income, never predicted income"} />
    </div>
  );
};

export default AccelSummary;
